import { Alert, Chip, Tab, Tabs } from '@mui/material';
import { ActiveFilters } from './activeFilters';
import { FileImage } from './image';
import {
  AutocompleteInput, Datagrid, List, NumberField, ReferenceInput, SearchInput, SelectInput,
  TextField, useListContext, useRecordContext, type RaRecord
} from 'react-admin';

/**
 * Products, with the filters the old screen had - minus the Meplato sync.
 *
 * "Refilled" is a column and not just a filter because it decides which of the
 * two group discount percentages applies. Someone scanning this list to check
 * pricing needs to see it without opening each product.
 */
/**
 * The groups the shop actually thinks in.
 *
 * Ids rather than slugs, because product_category_lookup joins category.id.
 * Inks and ribbons are not a product type of their own - they are type 'toner'
 * like the toners, told apart only by category - so they need tabs of their
 * own or nothing on this screen distinguishes them. The list opens unfiltered:
 * a default tab hid everything outside it from whoever did not think to switch.
 */
const CATEGORY = { toners: 2, inks: 3, ribbons: 4, printers: 7, paper: 18 } as const;

const filters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Kód, tag alebo názov" />,
  <SelectInput
    key="type"
    source="type"
    label="Typ"
    choices={[
      { id: 'toner', name: 'Náplň do tlačiarne' },
      { id: 'printer', name: 'Tlačiareň' },
      { id: 'other', name: 'Iné' }
    ]}
  />,
  <SelectInput
    key="renewed"
    source="renewed"
    label="Originál / renovovaný"
    choices={[
      { id: 'original', name: 'Originál' },
      { id: 'renewed', name: 'Renovovaný' }
    ]}
  />,
  <SelectInput
    key="producer"
    source="producer"
    label="Výrobca"
    choices={[
      { id: 'assigned', name: 'Priradený' },
      { id: 'unassigned', name: 'Nepriradený' }
    ]}
  />,
  <ReferenceInput key="organization" source="organization_id" reference="organizations" label="Organizácia">
    <SelectInput optionText="name" label="Organizácia" />
  </ReferenceInput>,
  /*
   * "What fits this printer". The list it searches is restricted to printers,
   * because picking a toner here would ask which toners fit a toner and answer
   * with nothing - a filter that silently returns an empty screen.
   */
  <ReferenceInput
    key="forPrinter"
    source="forPrinter"
    reference="products"
    label="Náplne pre tlačiareň"
    filter={{ category: CATEGORY.printers }}
    perPage={25}
  >
    <AutocompleteInput
      label="Náplne pre tlačiareň"
      optionText={(record: { heading?: string | null; code?: string | null }) =>
        record?.heading ?? record?.code ?? ''}
      filterToQuery={(text: string) => ({ q: text })}
      sx={{ minWidth: 260 }}
    />
  </ReferenceInput>
];

const TABS = [
  { id: String(CATEGORY.toners), label: 'Tonery' },
  { id: String(CATEGORY.inks), label: 'Atramenty' },
  { id: String(CATEGORY.ribbons), label: 'Pásky' },
  { id: String(CATEGORY.printers), label: 'Tlačiarne' },
  { id: String(CATEGORY.paper), label: 'Papier' },
  { id: '', label: 'Všetko' }
];

function CategoryTabs() {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = String(filterValues['category'] ?? '');

  return (
    <>
      <Tabs
        value={TABS.some(tab => tab.id === current) ? current : ''}
        onChange={(_, value: string) => {
          const next = { ...filterValues };
          if (value === '') delete next['category'];
          else next['category'] = value;
          // Choosing a printer's consumables and a category at once asks two
          // different questions; the tab wins and the printer filter is cleared.
          delete next['forPrinter'];
          setFilters(next, displayedFilters);
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map(tab => <Tab key={tab.id} value={tab.id} label={tab.label} />)}
      </Tabs>
      <PrinterNotice />
    </>
  );
}

/** Says why the list narrowed, since the tab no longer explains it. */
function PrinterNotice() {
  const { filterValues } = useListContext();
  if (filterValues['forPrinter'] == null) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
      Zobrazujú sa náplne, ktoré pasujú do vybranej tlačiarne. Zrušíte to odstránením filtra
      „Náplne pre tlačiareň“.
    </Alert>
  );
}

function ImageField() {
  const record = useRecordContext<RaRecord & { fileIdentifier: string | null; heading: string | null }>();
  return <FileImage id={record?.fileIdentifier} alt={record?.heading ?? ''} />;
}

function RefilledField() {
  const record = useRecordContext<RaRecord & { repassed: boolean; type: string | null }>();
  if (record == null) return null;

  // Only meaningful for toners; a printer is neither original nor refilled.
  if (record.type !== 'toner') return null;

  return record.repassed
    ? <Chip size="small" color="secondary" label="Renovovaný" />
    : <Chip size="small" label="Originál" variant="outlined" />;
}

export function ProductList() {
  return (
    <List
      filters={filters}
      sort={{ field: 'id', order: 'DESC' }}
      perPage={25}
      title="Produkty"
    >
      <CategoryTabs />
      {/* The category is a tab, so it is already visible; the rest is not. */}
      <ActiveFilters
        ignore={['category']}
        labels={{
          q: { label: 'Hľadanie' },
          type: { label: 'Typ' },
          renewed: { label: 'Pôvod' },
          producer: { label: 'Výrobca' },
          organization_id: { label: 'Organizácia' },
          forPrinter: { label: 'Náplne pre tlačiareň' }
        }}
      />
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <ImageField />
        <TextField source="code" label="Kód" emptyText="—" />
        <TextField source="heading" label="Názov" emptyText="—" />
        <TextField source="type" label="Typ" />
        <RefilledField />
        <TextField source="warehouseStatus" label="Dostupnosť" emptyText="—" />
        <NumberField source="points" label="Body" />
      </Datagrid>
    </List>
  );
}
